import React from 'react';
import { Check, Flag, Info, Sparkles } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
const CODEX_URL = 'https://www.fao.org/ag/humannutrition/32444-09f5545b8abe9a0c3baf01a4502ac36e4.pdf';
const AO_URL = 'https://www.fda.gov.ph/wp-content/uploads/2021/03/Administrative-Order-No.-2014-0030.pdf';
type Suggestion = {
  claim: string;
  tag: string;
  rationale: string;
  flag?: string;
  citations: {
    text: string;
    url: string;
  }[];
  trackedLabel?: string;
  trackedTarget?: 'claims' | 'label';
};
const ready: Suggestion[] = [{
  claim: 'HIGH IN PROTEIN',
  tag: 'Nutrition',
  rationale: "26.9g/100g ≈ 37.9% NRV — clears the ≥20% NRV/100g (14.2g/100g) 'high in' threshold with room to spare.",
  citations: [{
    text: 'Codex CAC/GL 23-1997',
    url: CODEX_URL
  }]
}, {
  claim: 'SOURCE OF PROTEIN',
  tag: 'Nutrition',
  rationale: "26.9g/100g clears the ≥10% NRV/100g (7.1g/100g) 'source of' threshold easily.",
  citations: [{
    text: 'Codex CAC/GL 23-1997',
    url: CODEX_URL
  }]
}, {
  claim: 'NO ADDED SUGAR',
  tag: 'Compositional',
  rationale: 'No added sugar or sweetener ingredient appears anywhere in the ingredient list.',
  citations: [{
    text: 'AO 2014-0030',
    url: AO_URL
  }]
}, {
  claim: 'Protein helps build and repair body tissues',
  tag: 'Nutrient function',
  rationale: "Codex-recognized function-claim template — qualifies given the product's protein source/high status above.",
  citations: [{
    text: 'Codex CAC/GL 23-1997 §2.1.3',
    url: CODEX_URL
  }]
}, {
  claim: 'Calcium aids in the development of strong bones and teeth',
  tag: 'Nutrient function',
  rationale: "Codex-recognized function-claim template — qualifies given the product's calcium source status.",
  citations: [{
    text: 'Codex CAC/GL 23-1997 §2.1.3',
    url: CODEX_URL
  }]
}];
const conditional: Suggestion[] = [{
  claim: 'HIGH IN CALCIUM',
  tag: 'Nutrition',
  flag: 'Needs: confirm exact PDRI 2015 calcium reference before use',
  rationale: "659mg/100g likely clears the roughly 2–3× 'source' threshold used for a 'high in' claim, but the precise PDRI 2015 calcium reference value hasn't been pinned down yet. Confirm before claiming.",
  citations: [{
    text: 'Codex CAC/GL 23-1997',
    url: CODEX_URL
  }]
}];
const needsFix: Suggestion[] = [{
  claim: '27% MORE PROTEIN',
  tag: 'Nutrition',
  rationale: 'Fails the absolute-difference test — the gap versus the reference product is under 6g/100g, short of the required 7.1g/100g. Remove the claim or substantiate a larger absolute gap.',
  citations: [{
    text: 'Codex CAC/GL 23-1997 §6.3',
    url: CODEX_URL
  }]
}, {
  claim: '57% LESS FAT',
  tag: 'Nutrition',
  rationale: "Footnote/symbol inconsistency between the claim's asterisk and the Nutrition table's caret, plus a possible 'close proximity' gap since the footnote sits on a different panel.",
  citations: [{
    text: 'Codex CAC/GL 23-1997 §6.2',
    url: CODEX_URL
  }]
}, {
  claim: 'HALAL',
  tag: 'Certification',
  rationale: 'Generic seal with no accredited certifying body named. Get NCMF-accredited certification with a logo or certificate number, or drop the claim.',
  citations: [{
    text: 'AO 2014-0030 §VI.B.4',
    url: AO_URL
  }, {
    text: 'RA 10817',
    url: 'https://lawphil.net/statutes/repacts/ra2016/ra_10817_2016.html'
  }]
}];
const onLabel = ['8G PROTEIN PER SERVE', 'SOURCE OF CALCIUM', 'NATURAL CHEESE', 'VEGETARIAN FRIENDLY', 'MADE IN NEW ZEALAND'];
export function PhSuggestedClaimsTab({
  onClaimsClick,
  onLabelClick
}: {
  onClaimsClick?: () => void;
  onLabelClick?: () => void;
}) {
  return <div className="space-y-5" data-id="element-2098" data-mp="2scidt5vqmxvq">
      <header className="flex items-start gap-3" data-id="element-2099" data-mp="2ncdz9lee4d85">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-2100" data-mp="2ndp0bdggb6vk">
          <Sparkles className="h-5 w-5" data-id="element-2101" data-mp="1bwfkr20g0yzj" />
        </span>
        <div data-id="element-2102" data-mp="1buuq7ce4pg1o">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-2103" data-mp="1byijqbjkq2ge">Suggested claims</h2>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-[#6b7d70]" data-id="element-2104" data-mp="2zr0wm2e65vbd">
            Claims this product may be eligible to make, based on its composition.
          </p>
        </div>
      </header>

      <p className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-2105" data-mp="2qqck5c4fc30l">
        {ready.length} ready to use · {conditional.length} conditional · {needsFix.length} need a fix · {onLabel.length} already on label
      </p>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900" data-id="element-2106" data-mp="3nig387czaysf">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-2107" data-mp="39i5bl23uhg67" />
        <span data-id="element-2108" data-mp="3ehi7eccp1xkx">
          Nutrient content and function claims are checked against the Codex Alimentarius significant-amount thresholds; comparative claims
          additionally require a stated reference food and direction. Two claims already flagged elsewhere on this product — 27% MORE PROTEIN and
          HALAL — are cross-referenced below rather than duplicated.
        </span>
      </div>

      <GroupHeader dot="bg-forest-green" label={`Ready to use · ${ready.length}`} data-id="element-2109" data-mp="1qks7u1ku9rbk" />
      <div className="space-y-3" data-id="element-2110" data-mp="24k086xsqn5hc">
        {ready.map(suggestion => <SuggestionCard key={suggestion.claim} suggestion={suggestion} data-id="element-2111" data-mp="24hxmgpymbyif" />)}
      </div>

      <GroupHeader dot="bg-amber-500" label={`Conditional · ${conditional.length}`} data-id="element-2112" data-mp="1ziugn3atdo9e" />
      <div className="space-y-3" data-id="element-2113" data-mp="1bvw0k6qlwic0">
        {conditional.map(suggestion => <SuggestionCard key={suggestion.claim} suggestion={suggestion} data-id="element-2114" data-mp="16xb50kmaumtw" />)}
      </div>

      <GroupHeader dot="bg-amber-500" label={`Needs a fix · ${needsFix.length}`} data-id="element-2115" data-mp="1lm7hctve2m0b" />
      <div className="space-y-3" data-id="element-2116" data-mp="1gu5z8rbji12d">
        {needsFix.map(suggestion => <SuggestionCard key={suggestion.claim} suggestion={suggestion} onTrackedClick={suggestion.trackedTarget === 'label' ? onLabelClick : onClaimsClick} data-id="element-2117" data-mp="t4v3jo91qwv3" />)}
      </div>

      <section className="rounded-2xl border border-[#e2e8e0] bg-[#f6f8f4] p-5" aria-labelledby="already-on-label" data-id="element-2118" data-mp="2o8t6f8x2n2zi">
        <h3 id="already-on-label" className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-2119" data-mp="2t5tcvkxu27r5">
          Already on your label · confirmed valid
        </h3>
        <ul className="mt-3 divide-y divide-[#e5eae2]" data-id="element-2120" data-mp="31yd5o4rg1i42">
          {onLabel.map(claim => <li key={claim} className="flex flex-wrap items-center justify-between gap-2 py-2" data-id="element-2121" data-mp="25a763w9dpfwn">
              <span className="text-sm text-[#5d6f62]" data-id="element-2122" data-mp="2a7gur9oy1njf">{claim}</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-green/10 px-2 py-0.5 text-xs font-semibold text-forest-green" data-id="element-2123" data-mp="2e9tzpvvv9hf5">
                <Check className="h-3 w-3" data-id="element-2124" data-mp="2jahxd0elowic" />
                Confirmed valid
              </span>
            </li>)}
        </ul>
        <button type="button" onClick={onClaimsClick} className="mt-3 text-xs font-semibold text-[#637667] transition hover:text-forest-green hover:underline" data-id="element-2125" data-mp="3prd8s4w4oelm">
          View full assessment → Claims tab
        </button>
      </section>

      <div className="flex items-start gap-2 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#607266]" data-id="element-2126" data-mp="3obft32gfvrf">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-2127" data-mp="3r15fgx0wvtvc" />
        Suggestions only — not a verification. Each claim must be confirmed and, where indicated, substantiated before use.
      </div>
    </div>;
}
function GroupHeader({
  dot,
  label
}: {
  dot: string;
  label: string;
}) {
  return <h3 className="flex items-center gap-2 pt-1 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#6f8174]" data-id="element-2128" data-mp="3c2ti0o5psh34">
      <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden="true" data-id="element-2129" data-mp="3832vwxr4up4x" />
      {label}
    </h3>;
}
function SuggestionCard({
  suggestion,
  onTrackedClick
}: {
  suggestion: Suggestion;
  onTrackedClick?: () => void;
}) {
  return <article className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-2130" data-mp="168abj4rkx66x">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-2131" data-mp="1pjy3hv1uq6av">
        <h4 className="font-[Figtree] text-base font-semibold leading-snug text-evergreen" data-id="element-2132" data-mp="1tk8anb1dr2rb">{suggestion.claim}</h4>
        <span className="shrink-0 rounded-md bg-[#f0f3ef] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#637467]" data-id="element-2133" data-mp="1ft37hyyszsc8">
          {suggestion.tag}
        </span>
      </div>
      {suggestion.flag && <p className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900" data-id="element-2134" data-mp="1au0bf40swwge">
          <Flag className="h-3.5 w-3.5" data-id="element-2135" data-mp="wwbq5v2tnnat" />
          {suggestion.flag}
        </p>}
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#56685c]" data-id="element-2136" data-mp="11u3s7hx6kpsq">{suggestion.rationale}</p>
      <div className="mt-4 flex flex-wrap gap-2" data-id="element-2137" data-mp="2r91kuf10l0j9">
        {suggestion.citations.map(citation => <RegulationLink key={citation.text} text={citation.text} href={citation.url} data-id="element-2138" data-mp="2w733wzyfwzuq" />)}
      </div>
      {suggestion.trackedLabel && <button type="button" onClick={onTrackedClick} className="mt-3 text-xs font-semibold text-[#637667] transition hover:text-forest-green hover:underline" data-id="element-2139" data-mp="31cp0yrdycsef">
          {suggestion.trackedLabel}
        </button>}
    </article>;
}